import { useQuery, useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useShop } from "@/contexts/ShopContext";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { CRO } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, TrendingDown, ArrowRight, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import CroActionControls from "@/components/CroActionControls";
import BulkPlanButton from "@/components/BulkPlanButton";
import RevenueImpact from "@/components/RevenueImpact";
import RetestAllButton from "@/components/RetestAllButton";

const priorityTone = {
  critical: "bg-destructive/15 text-destructive border-destructive/30",
  high: "bg-[hsl(var(--chart-2))]/15 text-[hsl(var(--chart-2))] border-[hsl(var(--chart-2))]/30",
  medium: "bg-[hsl(var(--chart-1))]/15 text-[hsl(var(--chart-1))] border-[hsl(var(--chart-1))]/30",
};

function fmtEur(n) {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
}
function fmtInt(n) {
  return new Intl.NumberFormat("en-US").format(Math.round(n || 0));
}

export default function CROPage() {
  const { selectedShopId, selectedShop } = useShop();
  const { t } = useLang();
  const [recs, setRecs] = useState(null);
  const [source, setSource] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const pollRef = useRef(null);

  const { data: funnel } = useQuery({
    queryKey: ["funnel", selectedShopId],
    queryFn: async () => (await api.get(`/shops/${selectedShopId}/funnel`)).data,
    enabled: !!selectedShopId,
  });

  const { data: croActions = [] } = useQuery({
    queryKey: ["cro-actions", selectedShopId],
    queryFn: async () => (await api.get(`/shops/${selectedShopId}/cro/actions`)).data,
    enabled: !!selectedShopId,
  });

  // Reset when shop changes
  useEffect(() => {
    setRecs(null);
    setSource(null);
    setTaskId(null);
    setTaskStatus(null);
    if (pollRef.current) clearInterval(pollRef.current);
  }, [selectedShopId]);

  const startMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/shops/${selectedShopId}/cro/recommendations/async`)).data,
    onSuccess: (data) => {
      setTaskId(data.task_id);
      setTaskStatus("pending");
      setRecs(null);
    },
    onError: () => toast.error("Failed to start CRO job"),
  });

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get(`/cro/tasks/${taskId}`);
        if (cancelled) return;
        setTaskStatus(res.data.status);
        if (res.data.status === "done") {
          const r = res.data.result || {};
          setRecs(r.recommendations || []);
          setSource(r.source);
          toast.success(
            r.source === "mock" ? "Recommendations ready (mock)" : "AI recommendations ready",
          );
          clearInterval(pollRef.current);
          setTaskId(null);
        } else if (res.data.status === "failed") {
          toast.error(res.data.error || "CRO task failed");
          clearInterval(pollRef.current);
          setTaskId(null);
        }
      } catch {
        /* retry on next tick */
      }
    };
    pollRef.current = setInterval(poll, 2000);
    poll();
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [taskId]);

  const isGenerating = !!taskId && (taskStatus === "pending" || taskStatus === "running");
  const maxVisitors = funnel?.steps?.[0]?.visitors || 1;

  return (
    <div data-testid={CRO.root} className="space-y-6 rk-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
            {selectedShop?.domain} · {funnel?.funnel_type}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            {t("cro_title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("cro_subtitle")}</p>
        </div>
        <Button
          data-testid={CRO.generateBtn}
          onClick={() => startMutation.mutate()}
          disabled={isGenerating || !funnel}
          className="h-11 gap-2"
        >
          <Sparkles className={cn("h-4 w-4", isGenerating && "animate-pulse")} />
          {isGenerating ? t("generating") : t("generate_recommendations")}
        </Button>
      </header>

      {isGenerating && (
        <div
          data-testid={CRO.progress}
          className="border border-border bg-card rounded-lg p-4 flex items-center gap-3 text-sm"
        >
          <div className="relative w-4 h-4">
            <div className="absolute inset-0 rounded-full border-2 border-accent-action/30" />
            <div className="absolute inset-0 rounded-full border-2 border-accent-action border-t-transparent animate-spin" />
          </div>
          <div className="font-mono text-muted-foreground">
            {t("generating")} · task <span className="text-foreground">{taskId?.slice(0, 12)}</span> · status {taskStatus}
          </div>
        </div>
      )}

      {/* Funnel KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile label={t("visitors")} value={fmtInt(funnel?.total_visitors)} hint={t("funnel")} />
        <KpiTile
          label={t("conversion_rate")}
          value={funnel ? `${(funnel.overall_conversion_rate * 100).toFixed(2)}%` : "—"}
          hint={`${fmtInt(funnel?.total_conversions)} · ${t("conversions")}`}
        />
        <KpiTile
          label={t("revenue")}
          value={fmtEur(funnel?.revenue_estimate_eur)}
          hint={`AOV ${fmtEur(funnel?.avg_order_value_eur)}`}
        />
        <KpiTile
          label={t("biggest_drop")}
          value={funnel ? `${funnel.biggest_drop_pct}%` : "—"}
          hint={funnel?.biggest_drop_step}
          tone="destructive"
        />
      </div>

      {/* Funnel steps */}
      <div data-testid={CRO.funnel} className="border border-border bg-card rounded-lg p-6">
        <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground mb-6">
          {t("funnel")}
        </div>
        <div className="space-y-4">
          {(funnel?.steps || []).map((s, i) => {
            const widthPct = (s.visitors / maxVisitors) * 100;
            const prevVisitors = i > 0 ? funnel.steps[i - 1].visitors : s.visitors;
            const stepDrop = i > 0 ? (1 - s.visitors / prevVisitors) * 100 : 0;
            const isBiggest = s.step_id === funnel?.biggest_drop_step;
            return (
              <div key={s.step_id} data-testid={CRO.step(s.step_id)}>
                <div className="flex items-center justify-between text-xs font-mono mb-1.5">
                  <span className="flex items-center gap-2">
                    <span className="text-muted-foreground w-4 text-right">{i + 1}.</span>
                    <span className="font-medium text-foreground">{s.label}</span>
                    {isBiggest && (
                      <Badge className="bg-destructive/15 text-destructive border-transparent text-[9px] font-mono uppercase tracking-wider">
                        drop
                      </Badge>
                    )}
                  </span>
                  <span className="tabular text-muted-foreground flex items-center gap-3">
                    {i > 0 && (
                      <span className="text-destructive">-{stepDrop.toFixed(1)}%</span>
                    )}
                    <span className="text-foreground">{fmtInt(s.visitors)}</span>
                  </span>
                </div>
                <div className="h-8 rounded-md bg-secondary overflow-hidden relative">
                  <div
                    className={cn(
                      "h-full rounded-md transition-[width] duration-500",
                      isBiggest ? "bg-destructive/70" : "bg-accent-action",
                    )}
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recommendations */}
      {recs && (
        <div data-testid={CRO.recommendationsList} className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-display text-xl font-medium tracking-tight">
              {t("ai_recommendations")}
            </h2>
            <div className="flex items-center gap-3">
              <RetestAllButton
                shopId={selectedShopId}
                onSuccess={() => {
                  // After the crawler + rankings refresh, immediately regenerate
                  // CRO recommendations so the user sees fresh AI proposals based
                  // on the updated audit data.
                  if (!isGenerating) startMutation.mutate();
                }}
              />
              <BulkPlanButton
                shopId={selectedShopId}
                recs={recs}
                actions={croActions}
              />
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                {t("powered_by")} {source === "mock" ? "Rankly mock" : "Claude Sonnet 4.5"}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {recs.map((r, i) => (
              <div
                key={i}
                data-testid={CRO.rec(i)}
                className="border border-border bg-card rounded-lg p-5 hover:-translate-y-0.5 transition-transform"
              >
                <div className="flex items-center justify-between mb-3">
                  <Badge
                    className={cn(
                      "text-[10px] font-mono uppercase tracking-widest border",
                      priorityTone[r.priority] ?? priorityTone.medium,
                    )}
                  >
                    {t(r.priority)}
                  </Badge>
                  <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
                    {t("effort")}: {t(r.effort)}
                  </div>
                </div>
                <h3 className="font-display text-lg font-medium tracking-tight mb-1">
                  {r.title}
                </h3>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">
                  step · {r.step}
                </div>

                <div className="space-y-2 text-sm">
                  <RecRow icon={TrendingDown} label={t("problem")} text={r.problem} />
                  <RecRow icon={Zap} label={t("hypothesis")} text={r.hypothesis} />
                  <RecRow icon={ArrowRight} label={t("experiment")} text={r.experiment} />
                </div>

                <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
                      {t("expected_uplift")}
                    </div>
                    <div className="font-mono text-2xl tabular text-[hsl(var(--chart-3))] mt-1">
                      +{r.expected_uplift_pct}%
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
                      {t("est_revenue_lift")}
                    </div>
                    <div className="font-mono text-2xl tabular mt-1">
                      {fmtEur(r.estimated_revenue_eur)}
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <RevenueImpact shopId={selectedShopId} upliftPct={r.expected_uplift_pct} />
                </div>

                <CroActionControls
                  shopId={selectedShopId}
                  shopDomain={selectedShop?.domain}
                  rec={r}
                  actions={croActions}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, value, hint, tone }) {
  const cls = tone === "destructive" ? "text-destructive" : "text-foreground";
  return (
    <div className="border border-border bg-card rounded-lg p-5">
      <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
        {label}
      </div>
      <div className={cn("font-mono text-3xl sm:text-4xl tabular tracking-tight mt-4", cls)}>
        {value}
      </div>
      {hint && <div className="text-xs font-mono text-muted-foreground mt-2">{hint}</div>}
    </div>
  );
}

function RecRow({ icon: Icon, label, text }) {
  return (
    <div className="flex gap-2.5">
      <Icon className="h-3.5 w-3.5 mt-1 text-muted-foreground shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] font-mono text-muted-foreground">
          {label}
        </div>
        <div className="text-sm text-foreground">{text}</div>
      </div>
    </div>
  );
}
